/**
 * Per-run citation registry. The timeline publishes references under a run id;
 * the action row and the citations panel read them. The artifact carries ids
 * only, because agents cannot import an Evidence reference type.
 *
 * Claiming hides the inline button when the latest turn's action row already
 * offers those runs.
 */

import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useId, useMemo } from 'react';

import type { EnrichedReference } from '../../contracts';
import { getCitationKey } from '../../contracts';

/**
 * References published by each Evidence child run. A run can have several
 * publishers (one per text group); each keeps its own list.
 */
const referencesByRunAtom = atom<Record<string, Record<string, EnrichedReference[]>>>({});

/** Run ids whose citations are already offered by a turn-level action row. */
const claimedRunIdsAtom = atom<ReadonlySet<string>>(new Set<string>());

/** Publish this run's references. Clears this publisher's list on unmount. */
export const usePublishEvidenceReferences = (
  runId: string | undefined,
  references: EnrichedReference[]
): void => {
  const publisherId = useId();
  const setReferences = useSetAtom(referencesByRunAtom);

  useEffect(() => {
    if (!runId) return;
    setReferences((prev) => ({
      ...prev,
      [runId]: { ...prev[runId], [publisherId]: references },
    }));
  }, [runId, references, publisherId, setReferences]);

  useEffect(() => {
    if (!runId) return;
    return () => {
      setReferences((prev) => {
        const slot = prev[runId];
        if (!slot || !(publisherId in slot)) return prev;
        const nextSlot = { ...slot };
        delete nextSlot[publisherId];
        const next = { ...prev };
        if (Object.keys(nextSlot).length === 0) delete next[runId];
        else next[runId] = nextSlot;
        return next;
      });
    };
  }, [runId, publisherId, setReferences]);
};

/** Merged, deduped references for these runs. */
export const useEvidenceCitations = (runIds: readonly string[]): EnrichedReference[] => {
  const byRun = useAtomValue(referencesByRunAtom);
  // Joined so a fresh array of the same ids does not re-merge on every render.
  const key = runIds.join('\u0000');

  return useMemo(() => {
    const seen = new Set<string>();
    const merged: EnrichedReference[] = [];
    for (const runId of key ? key.split('\u0000') : []) {
      for (const published of Object.values(byRun[runId] ?? {})) {
        for (const reference of published) {
          const citationKey = getCitationKey(reference);
          if (seen.has(citationKey)) continue;
          seen.add(citationKey);
          merged.push(reference);
        }
      }
    }
    return merged;
  }, [byRun, key]);
};

/** Claim these runs so an inline button for the same run hides itself. */
export const useClaimEvidenceCitationRuns = (runIds: readonly string[]): void => {
  const setClaimed = useSetAtom(claimedRunIdsAtom);
  const key = runIds.join('\u0000');

  useEffect(() => {
    const ids = key ? key.split('\u0000') : [];
    if (ids.length === 0) return;

    setClaimed((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });

    return () => {
      setClaimed((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
    };
  }, [key, setClaimed]);
};

/** True when every run is already offered by a turn-level action row. */
export const useAreEvidenceCitationRunsClaimed = (runIds: readonly string[]): boolean => {
  const claimed = useAtomValue(claimedRunIdsAtom);
  return runIds.length > 0 && runIds.every((runId) => claimed.has(runId));
};
